// Client texts (Slack pane step 1b, docs/client-texts.md): a practice's staff
// contacts and the two-way SMS thread with each. Practice Details reads and writes
// through this file and nothing else.
//
// The list endpoint masks the cell (`cell_masked`); only the thread GET returns the
// full number, for the person who opened that contact's chat. Nothing here names a
// patient, but an inbound body is the client's words and may, so the thread is
// never logged or echoed anywhere but the pane.

import { compactPhone } from "../outreach/smsNumberApi";

const API_URL = process.env.REACT_APP_API_URL || "";

export const MAX_BODY_LENGTH = 1600;
export const THREAD_LIMIT = 200;
export const MAX_CONTACTS = 3;

export const ROLES = [
  { value: "owner", label: "Owner" },
  { value: "office_manager", label: "Office manager" },
  { value: "other", label: "Other" },
];

export function roleLabel(role) {
  const found = ROLES.find((r) => r.value === role);
  return found ? found.label : role || "";
}

async function readError(response, fallback) {
  try {
    const body = await response.json();
    return { message: (body && body.error) || fallback, body: body || {} };
  } catch (err) {
    return { message: fallback, body: {} };
  }
}

async function request(url, options, fallback) {
  const response = await fetch(url, { credentials: "include", ...options });
  if (!response.ok) {
    const { message, body } = await readError(response, fallback);
    const error = new Error(message);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  const body = await response.json().catch(() => null);
  if (!body || typeof body !== "object") {
    const error = new Error("The response could not be read, so this may be incomplete. Try again.");
    error.status = response.status;
    error.body = {};
    throw error;
  }
  return body;
}

function withBody(method, payload) {
  return {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
}

const str = (value) => (typeof value === "string" && value.trim() ? value.trim() : null);
const num = (value) => (Number.isFinite(Number(value)) && value !== null && value !== "" ? Number(value) : null);

// Wire shape: {id, name, cell_masked, role, position, active}. The thread's
// `contact` adds `cell` (full E.164), which the list never carries.
export function normalizeContact(raw) {
  const c = raw && typeof raw === "object" ? raw : {};
  return {
    id: c.id ?? null,
    name: str(c.name) || "",
    cellMasked: str(c.cell_masked) || "",
    cell: str(c.cell),
    role: str(c.role) || "other",
    position: num(c.position),
    active: c.active !== false,
  };
}

// Wire shape: {id, practice_id, contact_id, direction, body, peer_last4,
// sent_by_name, provider_status, received_at, sent_at, read_at, read_by,
// test_mode, created_at}.
export function normalizeMessage(raw) {
  const m = raw && typeof raw === "object" ? raw : {};
  return {
    id: m.id ?? null,
    contactId: m.contact_id ?? null,
    direction: m.direction === "out" ? "out" : "in",
    body: typeof m.body === "string" ? m.body : "",
    peerLast4: str(m.peer_last4),
    sentByName: str(m.sent_by_name),
    status: str(m.provider_status),
    receivedAt: str(m.received_at),
    sentAt: str(m.sent_at),
    readAt: str(m.read_at),
    testMode: m.test_mode === true,
    createdAt: str(m.created_at),
  };
}

export function normalizeThread(body) {
  const b = body || {};
  const d = b.draft && typeof b.draft === "object" ? b.draft : null;
  return {
    contact: b.contact ? normalizeContact(b.contact) : null,
    messages: Array.isArray(b.messages) ? b.messages.map(normalizeMessage) : [],
    draft: d && str(d.body) ? { id: d.id ?? null, body: d.body, createdBy: str(d.created_by) || "Claude", createdAt: str(d.created_at) } : null,
    unread: num(b.unread) || 0,
  };
}

const contactsBase = (practiceId) => `${API_URL}/api/practices/${practiceId}/contacts`;
const messagesBase = (practiceId) => `${API_URL}/api/practices/${practiceId}/messages`;

// GET …/contacts → {contacts: [...]}, sorted by position.
export async function fetchContacts(practiceId) {
  const body = await request(contactsBase(practiceId), {}, "Could not load the contacts.");
  const list = Array.isArray(body.contacts) ? body.contacts.map(normalizeContact) : [];
  return list.sort((a, b) => (a.position ?? 99) - (b.position ?? 99));
}

// POST …/contacts {name, cell, role, position} → 201 the contact. 400 with the
// server's sentence for a bad cell or a taken position; 403 for a non-admin.
export async function createContact(practiceId, { name, cell, role, position }) {
  const payload = { name: (name || "").trim(), cell: compactPhone(cell), role };
  if (position) payload.position = Number(position);
  const body = await request(contactsBase(practiceId), withBody("POST", payload), "Could not add the contact.");
  return normalizeContact(body);
}

// PUT …/contacts/<id> {name?, cell?, role?, position?, active?} → the contact.
// Only the keys present in `patch` are sent, so an edit that leaves the cell
// blank keeps the number on file.
export async function updateContact(practiceId, contactId, patch) {
  const payload = {};
  if (patch.name !== undefined) payload.name = (patch.name || "").trim();
  if (patch.cell !== undefined && patch.cell !== null && String(patch.cell).trim()) payload.cell = compactPhone(patch.cell);
  if (patch.role !== undefined) payload.role = patch.role;
  if (patch.position !== undefined && patch.position !== null) payload.position = Number(patch.position);
  if (patch.active !== undefined) payload.active = patch.active === true;
  const body = await request(
    `${contactsBase(practiceId)}/${contactId}`,
    withBody("PUT", payload),
    "Could not save the contact.",
  );
  return normalizeContact(body);
}

// DELETE …/contacts/<id> → active = false (the row stays, so the thread does).
export async function removeContact(practiceId, contactId) {
  const body = await request(
    `${contactsBase(practiceId)}/${contactId}`,
    { method: "DELETE" },
    "Could not remove the contact.",
  );
  return body;
}

// GET …/messages?contact_id=&limit=200[&mark_read=1] → {contact (with the full
// cell), messages (oldest first), draft | null, unread}.
export async function fetchThread(practiceId, contactId, { markRead = false } = {}) {
  const params = new URLSearchParams({ contact_id: String(contactId), limit: String(THREAD_LIMIT) });
  if (markRead) params.set("mark_read", "1");
  const body = await request(`${messagesBase(practiceId)}?${params.toString()}`, {}, "Could not load the thread.");
  return normalizeThread(body);
}

// POST …/messages {contact_id, body, draft_id?} → 201 the outbound row (status
// queued | sent | failed). `draft_id` is sent only when the compose box was filled
// from a draft, so the server can mark it consumed.
export async function sendMessage(practiceId, { contactId, body, draftId }) {
  const payload = { contact_id: contactId, body: String(body || "") };
  if (draftId !== undefined && draftId !== null) payload.draft_id = draftId;
  const row = await request(messagesBase(practiceId), withBody("POST", payload), "Could not send the text.");
  return normalizeMessage(row);
}
